import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { User, LoginRequest, RegisterRequest, UserUniversityResponse } from "@/types";
import { authApi } from "@/api/auth";
import { usersApi } from "@/api/users";
import { reviewsApi } from "@/api/reviews";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<User | null>(null);
  const token = ref<string | null>(localStorage.getItem("token"));
  const loading = ref(false);
  const initialized = ref(false); // Track if auth state is initialized
  
  // Global review viewing tracking
  const viewedReviewIds = ref<Set<string>>(new Set());
  
  // Load viewed review IDs from localStorage on initialization
  const loadViewedReviewIds = () => {
    try {
      const stored = localStorage.getItem('viewedReviewIds');
      if (stored) {
        const ids = JSON.parse(stored);
        viewedReviewIds.value = new Set(ids);
        console.log('📚 [AuthStore] Loaded viewed review IDs from localStorage:', ids);
      }
    } catch (error) {
      console.warn('Failed to load viewed review IDs from localStorage:', error);
    }
  };
  
  // Save viewed review IDs to localStorage
  const saveViewedReviewIds = () => {
    try {
      const ids = Array.from(viewedReviewIds.value);
      localStorage.setItem('viewedReviewIds', JSON.stringify(ids));
      console.log('💾 [AuthStore] Saved viewed review IDs to localStorage:', ids);
    } catch (error) {
      console.warn('Failed to save viewed review IDs to localStorage:', error);
    }
  };
  
  // Load on initialization
  loadViewedReviewIds();
  
  // Initialize selectedUniversity from localStorage
  const storedUniversity = localStorage.getItem("selectedUniversity");
  const selectedUniversity = ref<UserUniversityResponse | null>(
    storedUniversity ? JSON.parse(storedUniversity) : null
  );
  const universityLoading = ref(false);
  const universityInitialized = ref(!!storedUniversity);
  const hasSelectedUniversity = computed(() => !!selectedUniversity.value);

  // Computed properties
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const currentUser = computed(() => user.value);

  // Premium access computed properties
  const hasPremiumAccess = computed(() => {
    if (!user.value) return false;

    // Check if user has premium access until a future date
    if (user.value.premiumAccessUntil) {
      const premiumUntil = new Date(user.value.premiumAccessUntil);
      const now = new Date();
      return premiumUntil > now;
    }

    return false;
  });

  const canViewAllReviews = computed(() => {
    return (
      hasPremiumAccess.value || (user.value?.hasReviewedThisMonth ?? false)
    );
  });

  const reviewLimit = computed(() => {
    return canViewAllReviews.value ? null : 10;
  });

  // Sync viewed reviews from backend on login
  const syncViewedReviews = async () => {
    if (!isAuthenticated.value) return;
    
    try {
      const status = await reviewsApi.getReviewViewStatus();
      // Merge backend records with local cache
      const backendIds = new Set(status.viewedReviewIds);
      viewedReviewIds.value = new Set([
        ...Array.from(viewedReviewIds.value),
        ...Array.from(backendIds)
      ]);
      saveViewedReviewIds();
      console.log('✅ Synced viewed reviews from backend:', status.viewCount);
    } catch (error) {
      console.warn('Failed to sync viewed reviews from backend:', error);
    }
  };

  // Global review viewing methods
  const addViewedReview = async (reviewId: string) => {
    viewedReviewIds.value.add(reviewId);
    saveViewedReviewIds(); // Save to localStorage
    
    // Sync to backend if authenticated
    if (isAuthenticated.value) {
      try {
        await reviewsApi.recordReviewView(reviewId);
        console.log('✅ Recorded review view to backend:', reviewId);
      } catch (error) {
        console.warn('Failed to record review view to backend:', error);
        // Keep local record even if backend sync fails
      }
    }
  };

  const getViewedReviewCount = computed(() => {
    return viewedReviewIds.value.size;
  });

  const hasReachedGlobalReviewLimit = computed(() => {
    if (canViewAllReviews.value) return false;
    return getViewedReviewCount.value >= (reviewLimit.value || 10);
  });

  const canViewReview = (reviewId: string) => {
    if (canViewAllReviews.value) return true;
    if (viewedReviewIds.value.has(reviewId)) return true;
    return getViewedReviewCount.value < (reviewLimit.value || 10);
  };

  // Methods
  const login = async (credentials: LoginRequest) => {
    try {
      loading.value = true;
      const response = await authApi.login(credentials);
      
      token.value = response.token;
      localStorage.setItem("token", response.token);

      // Fetch user info, university, and sync reviews in parallel after successful login
      await Promise.all([
        fetchCurrentUser(),
        fetchUserUniversity().catch((err) => {
          // Silently handle 404 - user might not have selected a university yet
          if (err?.response?.status !== 404) {
            console.warn("Failed to fetch university after login:", err);
          }
        }),
        syncViewedReviews().catch((err) => {
          console.warn("Failed to sync viewed reviews after login:", err);
        })
      ]);
      universityInitialized.value = true;

      return { success: true };
    } catch (error: any) {
      console.error("Login error:", error);

      // Extract meaningful error message
      let errorMessage = "Login failed";

      if (error.response) {
        // Server responded with an error status
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.status === 401) {
          errorMessage = "Invalid username/email or password";
        } else if (error.response.status === 403) {
          errorMessage = "Account is disabled or email not verified";
        } else {
          errorMessage = `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = "Network error. Please check your connection.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      loading.value = true;

      // Just register the user, don't try to login automatically
      await authApi.register(userData);

      // Registration successful, but user needs to verify email before login
      return { success: true };
    } catch (error: any) {
      console.error("Registration error:", error);

      // Extract meaningful error message
      let errorMessage = "Registration failed";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        // Check if the error message is directly in the response data
        errorMessage =
          typeof error.response.data === "string"
            ? error.response.data
            : errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  };

  const logout = () => {
    user.value = null;
    token.value = null;
    selectedUniversity.value = null;
    viewedReviewIds.value.clear(); // Clear view records
    localStorage.removeItem("token");
    localStorage.removeItem("selectedUniversity");
    localStorage.removeItem("viewedReviewIds"); // Clear persisted view records
    universityInitialized.value = false;
    // Clear session-based skip flag
    sessionStorage.removeItem("university_selection_skipped");
  };

  const loginWithToken = async (jwtToken: string) => {
    try {
      loading.value = true;
      token.value = jwtToken;
      localStorage.setItem("token", jwtToken);

      // Fetch user info and university in parallel with the new token
      await Promise.all([
        fetchCurrentUser(),
        fetchUserUniversity().catch((err) => {
          if (err?.response?.status !== 404) {
            console.warn("Failed to fetch university with token:", err);
          }
        })
      ]);
      universityInitialized.value = true;

      return { success: true };
    } catch (error: any) {
      console.error("Auto-login error:", error);
      logout(); // Clear invalid token
      return { success: false, error: "Auto-login failed" };
    } finally {
      loading.value = false;
    }
  };

  const fetchCurrentUser = async () => {
    if (!token.value) {
      initialized.value = true;
      return;
    }

    try {
      const userData = await authApi.getCurrentUser();
      user.value = userData;
      initialized.value = true;
    } catch (error: any) {
      console.error("Failed to fetch current user:", error);
      
      // Clear authentication state only if it's a real auth error (401/403)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else {
        // For network errors or timeouts, keep the token but clear user
        user.value = null;
        // Don't throw on network errors, allow app to continue
        console.warn("Network error fetching user, continuing without user data");
      }
      initialized.value = true;
    }
  };

  // Initialize auth state on startup
  const initializeAuth = async () => {
    // Prevent multiple simultaneous initializations
    if (initialized.value) {
      return;
    }
    
    if (token.value) {
      try {
        // Fetch user, university, and sync reviews in parallel for faster initialization
        await Promise.all([
          Promise.race([
            fetchCurrentUser(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Auth timeout')), 5000)
            )
          ]),
          fetchUserUniversity().catch((err) => {
            // Silently handle university fetch errors (404 is expected for new users)
            if (err?.response?.status !== 404) {
              console.warn("Failed to fetch university during init:", err);
            }
          }),
          syncViewedReviews().catch((err) => {
            console.warn("Failed to sync viewed reviews during init:", err);
          })
        ]);
        universityInitialized.value = true;
      } catch (error) {
        console.error("Failed to initialize auth:", error);
        // Don't logout on timeout, just log the error
        if (error instanceof Error && error.message !== 'Auth timeout') {
          // Only logout on real auth errors
          const err = error as any;
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            logout();
          }
        }
      }
    } else {
      // No token, mark as initialized immediately
      initialized.value = true;
      universityInitialized.value = true;
    }
  };

  const updateProfile = async (userData: Partial<User>) => {
    try {
      loading.value = true;
      const updatedUser = await authApi.updateUserProfile(userData);
      user.value = updatedUser;
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  const changePassword = async (passwordData: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    try {
      loading.value = true;
      await authApi.changePassword(passwordData);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  // Handle premium access after review submission
  const handleReviewSubmission = () => {
    if (user.value) {
      // Grant premium access for one month after publishing a review
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      user.value.premiumAccessUntil = oneMonthFromNow.toISOString();
      user.value.hasReviewedThisMonth = true;
      user.value.reviewCount = (user.value.reviewCount || 0) + 1;
    }
  };

  // University selection methods
  const selectUniversity = async (universityId: number) => {
    try {
      loading.value = true;
      await usersApi.setUserUniversity(universityId);
      await fetchUserUniversity();
      return { success: true };
    } catch (error: any) {
      console.error("Error selecting university:", error);
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  const fetchUserUniversity = async () => {
    universityLoading.value = true;
    try {
      const university = await usersApi.getUserUniversity();
      selectedUniversity.value = university;
      // Persist to localStorage
      if (university) {
        localStorage.setItem("selectedUniversity", JSON.stringify(university));
      } else {
        localStorage.removeItem("selectedUniversity");
      }
      return university;
    } catch (error: any) {
      // 404 is expected when user hasn't selected a university yet
      // Only log other errors
      if (error.status !== 404 && error.response?.status !== 404) {
        console.error("Error fetching user university:", error);
      }
      selectedUniversity.value = null;
      localStorage.removeItem("selectedUniversity");
      return null;
    } finally {
      universityLoading.value = false;
      universityInitialized.value = true;
    }
  };

  const clearUniversity = async () => {
    try {
      loading.value = true;
      await usersApi.clearUserUniversity();
      selectedUniversity.value = null;
      localStorage.removeItem("selectedUniversity");
      universityInitialized.value = true;
      return { success: true };
    } catch (error: any) {
      console.error("Error clearing university:", error);
      return { success: false, error: error.message };
    } finally {
      loading.value = false;
    }
  };

  // Initialize user info on startup
  initializeAuth();

  return {
    // State
    user,
    token,
    loading,
    initialized,
    selectedUniversity,
    hasSelectedUniversity,
    universityLoading,
    universityInitialized,
    viewedReviewIds,

    // Computed properties
    isAuthenticated,
    currentUser,
    hasPremiumAccess,
    canViewAllReviews,
    reviewLimit,
    getViewedReviewCount,
    hasReachedGlobalReviewLimit,

    // Methods
    login,
    loginWithToken,
    register,
    logout,
    fetchCurrentUser,
    initializeAuth,
    updateProfile,
    changePassword,
    handleReviewSubmission,
    
    // University methods
    selectUniversity,
    fetchUserUniversity,
    clearUniversity,
    
    // Review viewing methods
    addViewedReview,
    canViewReview,
    syncViewedReviews,
  };
});
